"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const axios_1 = require("axios");
const jwt = require("jsonwebtoken");
const uuid = require("uuid");
const base_url_1 = require("./base-url");
const header_1 = require("./helpers/header");
const logger_1 = require("./helpers/logger");
const nested_error_1 = require("./helpers/nested-error");
async function createCoamGroupForNewResource(resource, group, admin, accessToken, correlationId) {
    const groupId = await createCoamGroup(group.name, group.description, accessToken, correlationId);
    try {
        await addResourceToCoamGroup(resource, groupId, accessToken, correlationId);
        await addUserToCoamGroup(groupId, admin.principal, true, accessToken, correlationId);
        await setPrincipalRoles(groupId, admin.principal, [admin.role], accessToken, correlationId);
    }
    catch (err) {
        await deleteCoamGroupOnSetupError(group.name, groupId, accessToken, correlationId);
        throw err;
    }
    return groupId;
}
exports.createCoamGroupForNewResource = createCoamGroupForNewResource;
async function addResourceToCoamGroup(resource, groupId, accessToken, correlationId) {
    try {
        await manageCoamGroupResources([resource], [], groupId, accessToken, correlationId);
    }
    catch (err) {
        throw nested_error_1.nestError(`Adding ${resource.type} ${resource.id} to COAM group ${groupId} failed.`, err);
    }
}
exports.addResourceToCoamGroup = addResourceToCoamGroup;
async function manageCoamGroupResources(toAdd, toRemove, groupId, accessToken, correlationId) {
    const add = toAdd.map(r => ({
        resource_type: r.type,
        resource_identifier: r.id,
    }));
    const remove = toRemove.map(r => ({
        resource_type: r.type,
        resource_identifier: r.id,
    }));
    try {
        await axios_1.default.request({
            method: 'PATCH',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}/resources`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
            data: {
                add,
                remove,
            },
        });
    }
    catch (err) {
        throw nested_error_1.nestError(`Adding/Removing resources to COAM group ${groupId} failed.`, err);
    }
}
exports.manageCoamGroupResources = manageCoamGroupResources;
async function setCoamGroupResources(resources, groupId, accessToken, correlationId) {
    try {
        const coamGroup = await getCoamGroupDetails(groupId, accessToken, correlationId);
        const toRemove = coamGroup.resources.filter(currentResource => !resources.some(r => r.id === currentResource.resource_identifier &&
            r.type === currentResource.resource_type));
        const toAdd = resources
            .filter(newResource => !coamGroup.resources.some(r => r.resource_identifier === newResource.id &&
            r.resource_type === newResource.type))
            .map(r => ({ resource_type: r.type, resource_identifier: r.id }));
        await axios_1.default.request({
            method: 'PATCH',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}/resources`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
            data: {
                remove: toRemove,
                add: toAdd,
            },
        });
    }
    catch (err) {
        throw nested_error_1.nestError(`Setting resources for COAM group ${groupId} failed.`, err);
    }
}
exports.setCoamGroupResources = setCoamGroupResources;
async function findCoamGroupByResourceAndName(resource, groupName, accessToken, correlationId) {
    const response = (await axios_1.default.request({
        url: `${base_url_1.getBaseUrl()}/groups?resource_type=${resource.type}` +
            `&resource_identifier=${resource.id}` +
            `&cacheBust=${uuid()}`,
        headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
    })).data;
    if (!Array.isArray(response.groups)) {
        return reject('response does not contain array of groups');
    }
    const groups = response.groups.filter((g) => g.name === groupName);
    if (groups.length === 0) {
        return undefined;
    }
    if (groups.length === 1) {
        return groups[0];
    }
    const groupIds = groups.map(group => group.id).join(',');
    return reject(`Found multiple groups named '${groupName}' for ${resource.type} ${resource.id}: ${groupIds}`);
}
exports.findCoamGroupByResourceAndName = findCoamGroupByResourceAndName;
async function getCoamGroupDetails(groupId, accessToken, correlationId, canonicalizeUsers = false) {
    try {
        return (await axios_1.default.request({
            method: 'GET',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}`,
            params: {
                cacheBust: Math.random(),
                canonicalize: canonicalizeUsers,
            },
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
        })).data;
    }
    catch (err) {
        throw nested_error_1.nestError(`Error getting details about COAM group ${groupId}`, err.error);
    }
}
exports.getCoamGroupDetails = getCoamGroupDetails;
async function createCoamGroup(name, description, accessToken, correlationId) {
    let response;
    try {
        response = (await axios_1.default.request({
            method: 'POST',
            url: `${base_url_1.getBaseUrl()}/groups`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
            data: {
                name,
                description,
            },
        })).data;
    }
    catch (err) {
        throw nested_error_1.nestError('Creating COAM group failed', err.error);
    }
    if (!response.id) {
        return reject('response does not contain id');
    }
    return response.id;
}
async function addUserToCoamGroup(groupId, principal, isAdmin, accessToken, correlationId) {
    return addUsersToCoamGroup(groupId, [{ principal, is_admin: isAdmin }], accessToken, correlationId);
}
exports.addUserToCoamGroup = addUserToCoamGroup;
async function addUsersToCoamGroup(groupId, add, accessToken, correlationId) {
    try {
        await axios_1.default.request({
            method: 'PATCH',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}/members`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
            data: {
                add,
            },
        });
    }
    catch (err) {
        throw nested_error_1.nestError(`Adding users ${add
            .map(a => a.principal)
            .join(', ')} to COAM group ${groupId} failed`, err.error);
    }
}
exports.addUsersToCoamGroup = addUsersToCoamGroup;
async function removeUserFromCoamGroup(groupId, principal, accessToken, correlationId) {
    try {
        await axios_1.default.request({
            method: 'PATCH',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}/members`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
            data: {
                remove: [principal],
            },
        });
    }
    catch (err) {
        throw nested_error_1.nestError(`Removing user ${principal} from COAM group ${groupId} failed`, err.error);
    }
}
exports.removeUserFromCoamGroup = removeUserFromCoamGroup;
async function setPrincipalRoles(groupId, principal, roles, accessToken, correlationId) {
    principal = encodeURIComponent(principal);
    try {
        await axios_1.default.request({
            method: 'PUT',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}/members/${principal}/roles`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
            data: { roles },
        });
    }
    catch (err) {
        throw nested_error_1.nestError(`Setting ${principal} roles ${roles.join(',')} to COAM group ${groupId} failed`, err.error);
    }
}
exports.setPrincipalRoles = setPrincipalRoles;
async function deleteCoamGroup(groupName, groupId, accessToken, correlationId) {
    let group;
    try {
        group = (await axios_1.default.request({
            method: 'GET',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
        })).data;
    }
    catch (err) {
        throw nested_error_1.nestError(`Getting COAM group ${groupId} failed`, err.error);
    }
    if (group.name !== groupName) {
        return reject(`Refusing to delete group ${groupId} ` +
            `- it's name='${group.name}' does not match expected name '${groupName}'`);
    }
    const expectedCreatedBy = jwt.decode(accessToken).sub;
    if (group.created_by !== expectedCreatedBy) {
        return reject(`Refusing to delete group ${groupId} ` +
            `- it's created_by='${group.created_by}' does match expected client ID '${expectedCreatedBy}'`);
    }
    try {
        await axios_1.default.request({
            method: 'DELETE',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
        });
    }
    catch (err) {
        throw nested_error_1.nestError(`Deleting COAM group ${groupId} failed`, err.error);
    }
}
exports.deleteCoamGroup = deleteCoamGroup;
async function deleteCoamGroupOnSetupError(groupName, groupId, accessToken, correlationId) {
    try {
        await deleteCoamGroup(groupName, groupId, accessToken, correlationId);
    }
    catch (err) {
        logger_1.getLogger().warn({ correlationId }, `Failed to delete COAM group ${groupId} when group setup failed`);
    }
}
function reject(message) {
    return Promise.reject(new Error(message));
}
async function modifyGroupPermissions(groupId, data, accessToken, correlationId) {
    try {
        await axios_1.default.request({
            method: 'PATCH',
            url: `${base_url_1.getBaseUrl()}/groups/${groupId}/permissions`,
            headers: header_1.getAuthAndCorrelationHeaders(accessToken, correlationId),
            data,
        });
    }
    catch (err) {
        throw nested_error_1.nestError(`Modifying permissions ${JSON.stringify(data)} to group ${groupId} failed`, err.message || '');
    }
}
exports.modifyGroupPermissions = modifyGroupPermissions;
//# sourceMappingURL=coam-group.js.map